import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import * as actions from "../../../Redux/Actions";
import {
  showErrorToastMessage,
  showSuccessToastMessage,
} from "../../../component/Toasts/Toasts";

/**
 * Bulk-import designation weightage from a long-format sheet:
 *   Designation | KRA | Weightage   (one row per KRA, grouped by designation)
 *
 * Parsing happens in the browser; we send a grouped JSON payload to the
 * bulk-import endpoint, which imports the valid designations and reports the
 * skipped ones (partial import).
 */
const ImportWeightageModal = (props: any) => {
  const { onHide, onImported } = props;
  const dispatch = useDispatch();

  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<"idle" | "working" | "done">("idle");
  const [result, setResult] = useState<any>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Read a cell by any case/spacing of the expected header name.
  const pick = (row: any, name: string) => {
    const key = Object.keys(row).find(
      (k) => k.trim().toLowerCase() === name.toLowerCase()
    );
    return key ? row[key] : undefined;
  };

  // Group the flat rows by designation, preserving first-seen order.
  const buildGroups = (rows: any[]) => {
    const order: string[] = [];
    const map: Record<string, { designation: string; items: any[] }> = {};
    rows.forEach((r) => {
      const designation = String(pick(r, "Designation") ?? "").trim();
      const kra = String(pick(r, "KRA") ?? "").trim();
      const weight = pick(r, "Weightage");
      // Both are mandatory (the template repeats the designation on every row, no
      // carry-forward). Skip any row missing either so we never form an empty-
      // designation group or send an item with a blank KRA.
      if (!designation || !kra) return;
      if (!map[designation]) {
        map[designation] = { designation, items: [] };
        order.push(designation);
      }
      map[designation].items.push({ kra, weight });
    });
    return order.map((d) => map[d]);
  };

  const handleSubmit = async () => {
    if (!file) {
      showErrorToastMessage("Please choose a file first.");
      return;
    }
    setStatus("working");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      const groups = buildGroups(rows);
      if (!groups.length) {
        setStatus("idle");
        showErrorToastMessage("No rows found. Use the template's columns: Designation, KRA, Weightage.");
        return;
      }
      dispatch(
        actions.bulkImportDesignationKraAction({ groups }, (res: any) => {
          if (res?.error) {
            setStatus("idle");
            const msg =
              res?.error?.response?.data?.error?.error_description ||
              "Import failed. Please retry.";
            showErrorToastMessage(msg);
            return;
          }
          setResult(res?.data || { imported: 0, skipped: [] });
          setStatus("done");
          if (res?.data?.imported > 0) {
            showSuccessToastMessage(`${res.data.imported} designation(s) imported.`);
            onImported && onImported();
          }
        })
      );
    } catch (e) {
      setStatus("idle");
      showErrorToastMessage("Couldn't read the file. Please upload a valid .xlsx or .csv.");
    }
  };

  const downloadSkipped = () => {
    if (!result?.skipped?.length) return;
    const ws = XLSX.utils.json_to_sheet(
      result.skipped.map((s: any) => ({ Designation: s.designation, Reason: s.reason }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Skipped");
    XLSX.writeFile(wb, "Weightage Import - Skipped.xlsx");
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setStatus("idle");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="readable-form">
      {status !== "done" ? (
        <>
          <p style={{ color: "#6c757d" }}>
            Upload a sheet with columns <b>Designation</b>, <b>KRA</b>, <b>Weightage</b> — one row per
            KRA. Each designation's weightages must total 100%. Unknown KRAs are created; unknown
            designations are skipped.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.csv"
            className="form-control"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          <div className="modal-btn-wrapper mt-4 mb-2">
            <div>
              <button className="btn cancel-btn buttonsize" onClick={onHide}>
                Cancel
              </button>
            </div>
            <div>
              <button
                className="btn add-submit-button buttonsize"
                disabled={!file || status === "working"}
                onClick={handleSubmit}
              >
                {status === "working" ? "Importing…" : "Import"}
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <p>
            <b>{result?.imported || 0}</b> designation(s) imported
            {result?.skipped?.length ? (
              <>
                , <b style={{ color: "#fd7e14" }}>{result.skipped.length}</b> skipped.
              </>
            ) : (
              <> — all rows imported.</>
            )}
          </p>

          {result?.skipped?.length > 0 && (
            <div className="table table-responsive" style={{ maxHeight: 240, overflow: "auto" }}>
              <table className="table-main table table-sm">
                <thead className="table-head">
                  <tr className="table-head-row">
                    <th className="table-head-row" scope="col">Designation</th>
                    <th className="table-head-row" scope="col">Reason</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {result.skipped.map((s: any, i: number) => (
                    <tr className="table-body-row" key={i}>
                      <td>{s.designation}</td>
                      <td>{s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="modal-btn-wrapper mt-4 mb-2">
            <div>
              <button className="btn cancel-btn buttonsize" onClick={reset}>
                Import Another
              </button>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              {result?.skipped?.length > 0 && (
                <button className="btn cancel-btn buttonsize" onClick={downloadSkipped}>
                  Download Skipped
                </button>
              )}
              <button className="btn add-submit-button buttonsize" onClick={onHide}>
                Done
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ImportWeightageModal;
